import { type FieldMetadata, useInputControl } from "@conform-to/react";
import {
  Combobox as HeadlessCombobox,
  ComboboxInput as HeadlessComboboxInput,
  ComboboxOptions as HeadlessComboboxOptions,
} from "@headlessui/react";
import { Loading } from "@winamp/icons/Loading.js";
import { classNames } from "../../utils/classNames";
import { useForceUpdate } from "../../utils/useForceUpdate";
import type { IComboboxBaseProps } from "../Combobox";
import { Description, Label } from "../Fieldset";
import { Input } from "../Input";

export type ISearchListboxProps<TMultiple extends boolean | undefined> =
  IComboboxBaseProps & {
    description?: React.ReactNode;
    label?: React.ReactNode;
    disabled?: boolean;
    query?: string;
    multiple?: TMultiple;
    meta?: TMultiple extends true
      ? FieldMetadata<Array<string>>
      : FieldMetadata<string>;
    onChange?: TMultiple extends true
      ? (value: Array<string>) => void
      : (value: string) => void;
    value?: TMultiple extends true ? Array<string> : string;
    emptyOptionsMessage?: string;
    loading?: boolean;
  };

export function SearchListbox({
  "aria-label": ariaLabel,
  autoFocus,
  children: options,
  className,
  description,
  emptyOptionsMessage,
  label,
  loading,
  onChange,
  meta,
  multiple = false,
  placeholder,
  query,
  setQuery,
  value: propsValue,
  ...props
}: ISearchListboxProps<boolean | undefined>) {
  const forceUpdate = useForceUpdate();
  const control = meta
    ? // biome-ignore lint/correctness/useHookAtTopLevel: If 'meta' is not provided then 'useInputControl' should not be called at all
      useInputControl(meta)
    : {
        blur: undefined,
        focus: undefined,
        change: (value: string & Array<string>) => onChange?.(value),
        value: propsValue,
      };

  const handleOnChange = (value: string & Array<string>) => {
    control.change(value);
    onChange?.(value);

    setTimeout(() => {
      control.blur?.();
      forceUpdate();
    });
  };

  const isEmptyOptions =
    !options || (Array.isArray(options) && options.length === 0);

  return (
    <HeadlessCombobox
      multiple={multiple}
      onChange={handleOnChange}
      value={control.value ?? (multiple ? [] : "")}
      immediate={false}
      {...props}
    >
      {label && <Label>{label}</Label>}
      {description && <Description>{description}</Description>}
      <HeadlessComboboxInput
        aria-label={ariaLabel}
        autoFocus={autoFocus}
        data-slot="control"
        className={classNames(
          [
            // Basic layout
            "group relative block w-full",

            // Background color + shadow applied to inset pseudo element, so shadow blends with border in light mode
            "before:absolute before:inset-px before:rounded-[calc(theme(borderRadius.lg)-1px)] before:bg-white before:shadow",

            // Background color is moved to control and shadow is removed in dark mode so hide `before` pseudo
            "dark:before:hidden",

            // Hide default focus styles
            "focus:outline-none",

            // Focus ring
            "after:pointer-events-none after:absolute after:inset-0 after:rounded-lg after:ring-inset after:ring-transparent sm:after:data-[focus]:ring-2 sm:after:data-[focus]:ring-focus",

            // Disabled state
            "data-[disabled]:opacity-50 before:data-[disabled]:bg-zinc-950/5 before:data-[disabled]:shadow-none",
          ],
          className,
        )}
        placeholder={placeholder}
        onChange={(event) => setQuery?.(event.currentTarget.value)}
        displayValue={() => ""}
        id={meta?.id}
        as={Input}
        icon={
          loading ? (
            <Loading className={classNames("animate-spin")} />
          ) : undefined
        }
      />
      <HeadlessComboboxOptions
        data-slot="control"
        as="div"
        id="search-listbox-options"
        className="mt-4 rounded-xl border border-primary bg-form-primary p-2 flex gap-2 flex-col max-h-64 overflow-y-auto"
        static
      >
        {isEmptyOptions ? (
          <div className="text-center text-secondary px-4 py-3 text-sm">
            {emptyOptionsMessage ?? `No results for your query "{query}".`}
          </div>
        ) : (
          options
        )}
      </HeadlessComboboxOptions>
    </HeadlessCombobox>
  );
}
